import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, RefreshCw, Gem, CheckCircle2, Home, X, HelpCircle, TrendingUp, Package, Edit3, FileText } from 'lucide-react'
import useContractStore from '../store/useContractStore'
import useAppStore from '../store/useAppStore'
import HelpModal from '../components/HelpModal'
import SeedEditor, { resetSavedSeed } from '../components/SeedEditor'
import AnimatedButton from '../components/AnimatedButton'
import confetti from 'canvas-confetti'
import useAudioManager from '../hooks/useAudioManager'
import { hapticImpact, hapticNotify, hapticSelectionChanged } from '../utils/haptic'

const Contract = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { loadProfile } = useAppStore()
  
  const { 
    availableItems, 
    selectedItems,
    contractResult,
    isLoading, 
    loadAvailableItems,
    toggleItemSelection,
    clearSelection,
    getTotalCost,
    canStartContract,
    getButtonText,
    startContract,
    resetContract
  } = useContractStore()

  const [currentStep, setCurrentStep] = useState(1)
  const [isAnimating, setIsAnimating] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const [isSeedEditorOpen, setIsSeedEditorOpen] = useState(false)
  
  // Инициализация Audio Manager через глобальный audioService
  const { playSound } = useAudioManager({
    contractSign: 'contractSign',
    contractWin: 'contractWin',
    BigWin: 'winV1',
    pick: 'pick',
    select: 'select'
  })

  // Звуки теперь управляются через useAudioManager (Web Audio API)
  // Предзагрузка происходит автоматически в хуке

  // Загрузка доступных предметов при монтировании и сброс выбора
  useEffect(() => {
    // Сбрасываем выбор при каждом заходе на страницу
    resetContract()
    // Загружаем актуальный список предметов
    loadAvailableItems()
    
    // Также сбрасываем при возвращении на страницу (visibility change)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        resetContract()
        loadAvailableItems()
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  // Прокрутка вверх при смене шага
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [currentStep])

  // Управление URL параметром hideUI для скрытия навигации
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    
    // Скрываем UI на шагах 3 и 4 (анимация и результат)
    if (currentStep === 3 || currentStep === 4) {
      if (searchParams.get('hideUI') !== 'true') {
        searchParams.set('hideUI', 'true')
        navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true })
      }
    } else {
      if (searchParams.has('hideUI')) {
        searchParams.delete('hideUI')
        const newSearch = searchParams.toString()
        navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true })
      }
    }
  }, [currentStep, location.pathname, navigate])

  // Все звуки теперь воспроизводятся через playSound() из useAudioManager
  // Это обеспечивает надежное воспроизведение через Web Audio API

  // Обработка клика по предмету
  const handleItemClick = (item) => {
    playSound('pick')
    hapticSelectionChanged()
    toggleItemSelection(item)
  }

  // Проверка, выбран ли предмет
  const isItemSelected = (itemId) => {
    return selectedItems.some(item => item.id === itemId)
  }

  // Обработка запуска контракта
  const handleStartContract = async () => {
    if (!canStartContract()) return

    try {
      // Воспроизводим звук выбора
      playSound('select')
      // Уверенный импульс при переходе к подтверждению
      hapticImpact('medium')
      // Переходим на шаг подтверждения
      setCurrentStep(2)
    } catch (error) {
      console.error('❌ Ошибка:', error)
      hapticNotify('error')
    }
  }

  // Подтверждение и выполнение контракта
  const handleConfirmContract = async () => {
    try {
      // Импульс на финальное подтверждение подписания контракта
      hapticImpact('medium')

      setCurrentStep(3) // Переходим на шаг анимации
      setIsAnimating(true)
      
      // Сбрасываем сохраненный токен перед подписанием контракта
      resetSavedSeed()
      
      // Отправляем цель в Яндекс.Метрику
      if (window.ym) {
        window.ym(104258304, 'reachGoal', 'start-contract')
      }
      
      // Воспроизводим звук подписания контракта
      playSound('contractSign')

      // Выполняем контракт
      const result = await startContract()
      
      // Задержка для анимации (3 секунды)
      setTimeout(() => {
        setIsAnimating(false)
        setCurrentStep(4) // Переходим на результат
        
        // Запускаем конфетти только если окупаемость положительная
        if (result && result.win && result.win.profit > 0) {
          // Воспроизводим звук крупной победы с конфетти
          playSound('BigWin')
          // Акцент на сильный положительный исход
          hapticImpact('heavy')
          hapticNotify('success')
          
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
            colors: ['#00FF7F', '#4EFFC6', '#FFD700']
          })
        } else {
          // Воспроизводим обычный звук выигрыша
          playSound('contractWin')
          // Нейтральный/слабый исход: только success-нотификация
          hapticNotify('success')
        }
      }, 3000)
    } catch (error) {
      console.error('❌ Ошибка выполнения контракта:', error)
      hapticNotify('error')
      setCurrentStep(2) // Возвращаемся на подтверждение
      setIsAnimating(false)
    }
  }

  // Сброс и начало нового контракта
  const handleReset = async () => {
    setCurrentStep(1)
    setIsAnimating(false)
    resetContract()
    
    try {
      await loadAvailableItems()
      await loadProfile()
    } catch (error) {
      console.error('❌ Ошибка обновления данных:', error)
    }
  }

  return (
    <div className="min-h-screen bg-dark-primary text-white">
      {/* Sticky Header - скрываем на шагах 3 и 4 */}
      {currentStep !== 3 && currentStep !== 4 && (
        <div className="top-0 z-10 bg-dark-primary/95 backdrop-blur-sm border-b border-white/10 px-6 py-3 lg:border-none">
          <div className="flex items-center gap-4 justify-center">
            {currentStep === 2 && (
              <button 
                onClick={() => {
                  if (currentStep === 2) {
                    setCurrentStep(1)
                  } else {
                    navigate('/games')
                  }
                }}
                className="p-2 rounded-lg bg-surface hover:bg-opacity-80 transition-all"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <div className="flex items-center gap-2">
              <h1 className="flex items-center gap-2 text-2xl font-bold text-accent-primary lg:text-4xl">
                <FileText className="w-6 h-6 shrink-0 lg:w-8 lg:h-8" />
                <span>{t('contract.title')}</span>
              </h1>
              <button
                onClick={() => setShowHelp(true)}
                className="p-1.5 rounded-full bg-dark-secondary/50 hover:bg-dark-secondary transition-colors"
              >
                <HelpCircle className="w-5 h-5 text-text-secondary" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="px-6">
        
        {/* ШАГ 1: Выбор скинов (от 3 до 10) */}
        {currentStep === 1 && (
          <div className="space-y-6 pt-6">
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-white">
                {t('contract.selectSkins')}
              </h2>
            </div>
            
            {/* Состояние загрузки */}
            {isLoading && (
              <div className="flex justify-center items-center py-12">
                <RefreshCw className="animate-spin text-accent-primary" size={32} />
              </div>
            )}

            {/* Список доступных скинов */}
            {!isLoading && (availableItems.length > 0 ? (
              <div className="flex flex-wrap justify-center gap-2 pb-48">
                {availableItems.map((skin) => (
                  <button
                    key={skin.id}
                    onClick={() => handleItemClick(skin)}
                    className={`relative w-[calc((100%-1rem)/3)] lg:w-[calc((100%-2.5rem)/6)] bg-surface rounded-lg border transition-all ${
                      isItemSelected(skin.id)
                        ? 'border-accent-primary shadow-lg shadow-accent-primary/30'
                        : 'border-white/10 hover:border-white/20'
                    }`}
                  >
                    {/* Индикатор выбора */}
                    {isItemSelected(skin.id) && (
                      <div className="absolute top-1 left-1 z-10">
                        <div className="w-5 h-5 rounded-full bg-accent-primary flex items-center justify-center">
                          <CheckCircle2 size={12} className="text-dark-primary" />
                        </div>
                      </div>
                    )}
                    
                    {/* Изображение скина */}
                    <div className="aspect-square p-2 flex items-center justify-center">
                      <img 
                        src={skin.img} 
                        alt={skin.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    
                    {/* Название (первая часть до |) */}
                    <div className="px-2 pb-2">
                      <p className="text-xs text-white font-medium truncate">
                        {skin.name.split('|')[0].trim()}
                      </p>
                      
                      {/* Цена */}
                      <div className="flex items-center justify-center gap-1 mt-1">
                        <Gem size={12} className="text-accent-primary" />
                        <span className="text-xs font-bold text-accent-primary">
                          {parseInt(skin.price).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              // Пустое состояние - нет доступных скинов
              <div className="text-center py-12">
                <div className="mb-6 flex justify-center">
                  <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                    <Gem className="w-10 h-10 text-primary" />
                  </div>
                </div>
                <p className="text-text-secondary mb-4 text-lg">
                  {t('contract.noItems')}
                </p>
                <p className="text-text-secondary mb-6">
                  {t('contract.noItemsDescription')}
                </p>
                <button
                  onClick={() => navigate('/cases')}
                  className="px-6 py-3 bg-accent-primary text-dark-primary font-bold rounded-lg hover:bg-accent-primary/90 transition-colors shadow-lg shadow-accent-primary/30"
                >
                  {t('contract.openCases')}
                </button>
              </div>
            ))}

            {/* Нижняя панель с информацией и кнопкой */}
            {!isLoading && availableItems.length > 0 && (
              <div className="fixed bottom-16 left-0 right-0 bg-dark-primary/95 backdrop-blur-sm border-t border-white/10 p-4 z-50 lg:bottom-0">
                <div className="max-w-md mx-auto space-y-3">
                  {/* Информация о выборе */}
                  {selectedItems.length > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-3">
                        <span className="text-text-secondary">
                          {t('contract.selected')}: {selectedItems.length}/10
                        </span>
                        <button
                          onClick={resetContract}
                          className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300 transition-colors"
                        >
                          <X size={14} />
                          <span>{t('contract.reset')}</span>
                        </button>
                      </div>
                      <div className="flex items-center gap-1 text-accent-primary font-bold">
                        <Gem size={16} />
                        <span>{getTotalCost().toLocaleString()}</span>
                      </div>
                    </div>
                  )}

                  {/* Кнопка контракта */}
                  <button
                    onClick={handleStartContract}
                    disabled={!canStartContract()}
                    className={`w-full h-14 font-bold text-lg rounded-xl transition-all ${
                      canStartContract()
                        ? 'bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary hover:opacity-90 shadow-lg shadow-accent-primary/30'
                        : 'bg-surface/50 text-text-secondary cursor-not-allowed'
                    }`}
                  >
                    {getButtonText()}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ШАГ 2: Подтверждение */}
        {currentStep === 2 && (
          <div className="min-h-[calc(100vh-140px)] flex items-center justify-center px-4 pt-6 pb-24">
            <div className="w-full space-y-6 max-w-md">
              <h2 className="text-2xl font-bold text-white text-center">
                {t('contract.confirm')}
              </h2>

              {/* Выбранные скины */}
              <div className="bg-surface rounded-lg p-4 border border-white/10">
                {/* <p className="text-sm text-text-secondary mb-3 text-center">
                  Вы обмениваете {selectedItems.length} {selectedItems.length === 1 ? 'предмет' : selectedItems.length < 5 ? 'предмета' : 'предметов'}
                </p> */}
                
                <div className="grid grid-cols-5 gap-2 mb-3">
                  {selectedItems.map((item) => (
                    <div key={item.id} className="aspect-square rounded overflow-hidden bg-dark-primary border border-white/10">
                      <img 
                        src={item.img} 
                        alt={item.name}
                        className="w-full h-full object-contain p-1"
                      />
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-center gap-2 pt-2 border-t border-white/10">
                  <div className="flex items-center gap-1 text-accent-primary font-bold">
                    <Gem size={18} />
                    <span className="text-lg">{getTotalCost().toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Кнопки */}
              <div className="space-y-3">
                <AnimatedButton onClick={handleConfirmContract}>
                  {t('contract.signContract')}
                </AnimatedButton>
                
                {/* Ссылка редактирования токена открытия */}
                <div className="text-center pt-4">
                  <button
                    onClick={() => setIsSeedEditorOpen(true)}
                    className="text-text-secondary hover:text-accent-primary transition-colors inline-flex items-center gap-2 text-sm"
                  >
                    <Edit3 size={16} />
                    {t('seedEditor.editSeed')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ШАГ 3: Анимация контракта */}
        {currentStep === 3 && (
          <div 
            className="fixed inset-0 bg-dark-primary/95 bg-center bg-no-repeat backdrop-blur-sm z-50 flex items-center justify-center p-6 lg:bg-cover"
            style={{
              backgroundImage: 'url(/images/roulette-background.png)'
            }}
          >
            <div className="text-center space-y-6">
              {/* Анимация загрузки */}
              <div className="relative w-32 h-32 mx-auto">
                <div className="absolute inset-0 border-4 border-accent-primary/30 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-accent-primary border-t-transparent rounded-full animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Gem size={48} className="text-accent-primary animate-pulse" />
                </div>
              </div>
              
              {/* <div className="space-y-2">
                <h2 className="text-2xl font-bold text-accent-primary">
                  {t('contract.signing')}
                </h2>
              </div> */}
            </div>
          </div>
        )}

        {/* ШАГ 4: Результат контракта */}
        {currentStep === 4 && contractResult && (
          <div 
            className="fixed inset-0 bg-dark-primary/95 bg-center bg-no-repeat backdrop-blur-sm z-50 flex items-center justify-center p-6 overflow-y-auto lg:bg-cover"
            style={{
              backgroundImage: 'url(/images/roulette-background.png)'
            }}
          >
            <div className="rounded-2xl max-w-md w-full relative my-auto">
              {/* Заголовок */}
              <h2 className="text-3xl font-bold text-center mb-6 text-accent-primary">
                {t('contract.contractSigned')}
              </h2>

              {/* Результат - выигранный предмет */}
              <div className="relative mb-8 max-w-sm w-full mx-auto">
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-accent-primary/20 via-accent-secondary/30 to-accent-primary/20 rounded-lg blur-xl animate-pulse"></div>
                
                {/* Main Card */}
                <div className="relative bg-surface rounded-lg p-6 border-2 border-accent-primary/50 shadow-[0_0_30px_rgba(0,255,127,0.3)]">
                  <div className="text-center">
                    {/* Item Image */}
                    <div className="relative w-32 h-32 mx-auto mb-4">
                      <div className="absolute inset-0 bg-accent-primary/20 rounded-lg blur-md animate-pulse"></div>
                      <div className="relative w-full h-full bg-dark-primary rounded-lg border border-accent-primary/50 overflow-hidden">
                        <img 
                          src={contractResult.win.img} 
                          alt={contractResult.win.name}
                          className="w-full h-full object-contain p-2"
                        />
                      </div>
                    </div>
                    
                    {/* Item Name */}
                    <h3 className="text-xl font-bold text-center mb-2 text-white">
                      {contractResult.win.name}
                    </h3>
                    
                    {/* Price and Profit */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-center gap-2 text-accent-primary font-bold text-lg">
                        <Gem size={20} />
                        <span>{contractResult.win.price.toLocaleString()}</span>
                      </div>
                      
                      {/* Profit Badge */}
                      <div className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${
                        contractResult.win.profit >= 0 
                          ? 'bg-accent-primary/20 text-accent-primary border border-accent-primary/30' 
                          : 'bg-red-500/20 text-red-400 border border-red-500/30'
                      }`}>
                        {contractResult.win.profit > 0 ? '' : ''}{contractResult.win.profit}%
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sparkle Effects */}
                <div className="absolute -top-4 -left-4 w-4 h-4 bg-accent-primary rounded-full animate-ping"></div>
                <div className="absolute -top-2 -right-6 w-2 h-2 bg-accent-secondary rounded-full animate-ping delay-300"></div>
                <div className="absolute -bottom-4 -right-4 w-3 h-3 bg-accent-primary rounded-full animate-ping delay-700"></div>
                <div className="absolute -bottom-2 -left-6 w-2 h-2 bg-accent-secondary rounded-full animate-ping delay-500"></div>
              </div>

              {/* Кнопки действий */}
              <div className="space-y-4 w-full max-w-sm mx-auto">
                {/* Главная кнопка - Апгрейдить */}
                <button
                  onClick={() => {
                    // Переходим в апгрейдер с ID выигранного предмета
                    navigate('/upgrader', { 
                      state: { 
                        fromContract: true,
                        itemId: contractResult.win.id 
                      } 
                    })
                  }}
                  className="w-full h-14 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold rounded-lg hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-lg shadow-accent-primary/30"
                >
                  <TrendingUp size={20} />
                  {t('caseOpening.upgradeDrop')}
                </button>
                
                {/* Вторичная кнопка - В инвентарь */}
                <button
                  onClick={() => navigate('/inventory')}
                  className="w-full h-14 bg-surface border border-accent-primary/30 text-white font-semibold rounded-lg hover:border-accent-primary/50 hover:bg-surface/80 transition-all flex items-center justify-center gap-2"
                >
                  <Package size={20} />
                  {t('contract.toInventory')}
                </button>

                {/* Текстовые ссылки */}
                <div className="flex items-center justify-center gap-6 pt-2 text-sm">
                  <button 
                    onClick={handleReset}
                    className="flex items-center gap-1.5 text-text-secondary hover:text-white transition-colors"
                  >
                    <RefreshCw size={16} />
                    <span>{t('contract.newContract')}</span>
                  </button>
                  
                  <button 
                    onClick={() => navigate('/')}
                    className="flex items-center gap-1.5 text-text-secondary hover:text-white transition-colors"
                  >
                    <Home size={16} />
                    <span>{t('contract.toHome')}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Модальное окно справки */}
      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title={t('contract.helpTitle')}
        steps={[
          t('contract.helpStep1'),
          t('contract.helpStep2'),
          t('contract.helpStep3'),
          t('contract.helpStep4')
        ]}
        buttonText={t('contract.helpGotIt')}
      />

      {/* Seed Editor Modal */}
      <SeedEditor
        isOpen={isSeedEditorOpen}
        onClose={() => setIsSeedEditorOpen(false)}
      />
    </div>
  )
}

export default Contract
